export interface Match {
  startIndex: number
  endIndex: number
  match: string
  marker?: HTMLElement
}

const regexCache = new Map<string, RegExp>()
const maxRegexCacheSize = 100

export function searchText(text: string, searchTerm: string, marker?: HTMLElement): Match[] {
  const matchMode = 'text'
  return findMatches(text, [searchTerm], matchMode, marker)
}

export function searchWord(text: string, word: string, marker?: HTMLElement): Match[] {
  const matchMode = 'word'
  return findMatches(text, [word], matchMode, marker)
}

export function searchAllWords(text: string, words: string[], marker?: HTMLElement): Match[] {
  const matchMode = 'word'
  return findMatches(text, words, matchMode, marker)
}

function findMatches(
  text: string,
  searchTexts: string[],
  matchMode: 'text' | 'word',
  marker?: HTMLElement
): Match[] {
  if (!text || text === '') return []
  if (marker && !isElement(marker)) return []
  if (!searchTexts?.length) return []

  const regex = getCachedRegex(matchMode, searchTexts)
  const matches = [...text.matchAll(regex)]

  return matches.map((match) => {
    let startIndex
    let matchedText
    if (matchMode === 'word') {
      startIndex = match.index + match[1].length
      matchedText = match[2]
    } else {
      startIndex = match.index
      matchedText = match[0]
    }

    return {
      startIndex,
      endIndex: startIndex + matchedText.length,
      match: matchedText,
      marker
    }
  })
}

function getCachedRegex(matchMode: 'text' | 'word', words: string[]): RegExp {
  const key = `${matchMode}:${words.join('\u0000')}`
  let regex = regexCache.get(key)
  if (!regex) {
    regex = matchMode === 'word' ? createWordRegex(words) : createHighlightRegex(words)
    if (regexCache.size >= maxRegexCacheSize) {
      const oldestKey = regexCache.keys().next().value
      if (oldestKey) regexCache.delete(oldestKey)
    }
    regexCache.set(key, regex)
  }
  regex.lastIndex = 0
  return regex
}

function isElement(obj: unknown): obj is HTMLElement {
  try {
    if (!obj) return false
    if (!(obj instanceof Node)) return false
    return obj instanceof (obj.ownerDocument?.defaultView?.HTMLElement || HTMLElement)
  } catch (e) {
    // Browsers not supporting W3 DOM2 don't have HTMLElement and
    // an exception is thrown and we end up here. Testing some
    // properties that all elements have (works on IE7)
    if (!obj || typeof obj !== 'object') return false
    const candidate = obj as { nodeType?: unknown; style?: unknown; ownerDocument?: unknown }
    return (
      candidate.nodeType === 1 &&
      typeof candidate.style === 'object' &&
      typeof candidate.ownerDocument === 'object'
    )
  }
}

function createHighlightRegex(words: string[] = []): RegExp {
  const escapedWords = words.map((word) => escapeRegEx(word))

  const regex = `(${escapedWords.join('|')})`
  return new RegExp(regex, 'g')
}

// Regex to find whole words within a string
//
// Unicode character blocks for letters.
// See: http://jrgraphix.net/research/unicode_blocks.php
//
// \\u0041-\\u005A    A-Z (Basic Latin)
// \\u0061-\\u007A    a-z (Basic Latin)
// \\u0030-\\u0039    0-9 (Basic Latin)
// \\u00AA            ª   (Latin-1 Supplement)
// \\u00B5            µ   (Latin-1 Supplement)
// \\u00BA            º   (Latin-1 Supplement)
// \\u00C0-\\u00D6    À-Ö (Latin-1 Supplement)
// \\u00D8-\\u00F6    Ø-ö (Latin-1 Supplement)
// \\u00F8-\\u00FF    ø-ÿ (Latin-1 Supplement)
// \\u0100-\\u017F    Ā-ſ (Latin Extended-A)
// \\u0180-\\u024F    ƀ-ɏ (Latin Extended-B)
const letterChars =
  '\\u0041-\\u005A\\u0061-\\u007A\\u0030-\\u0039\\u00AA\\u00B5\\u00BA\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u00FF\\u0100-\\u017F\\u0180-\\u024F'

function createWordRegex(words: string[] = []): RegExp {
  const escapedWords = words.map((word) => escapeRegEx(word))

  // (notLetter|^)(words)(?=notLetter|$)
  const regex = `([^${letterChars}]|^)` + `(${escapedWords.join('|')})` + `(?=[^${letterChars}]|$)`

  return new RegExp(regex, 'g')
}

function escapeRegEx(s: string): string {
  return String(s).replace(/([.*+?^=!:${}()|[\]/\\])/g, '\\$1')
}
